// Pure helpers that produce the data shown by the Important Dates UI.
// No React. No Firestore. Just date math + URL building.

const DATE_FIELDS = [
  { field: "inspectionDeadline", label: "Inspection Deadline" },
  { field: "rescissionDeadline", label: "Rescission Period Ends" },
  { field: "financingDeadline", label: "Financing Deadline" },
  { field: "closingdate", label: "Closing Date" },
];

const SHORT_LABEL = {
  inspectionDeadline: "Inspection Deadline",
  rescissionDeadline: "Rescission",
  financingDeadline: "Financing Deadline",
  closingdate: "Closing Date",
};

function _startOfToday() {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
}

function _parseISODate(s) {
  if (typeof s !== "string" || !s) return null;
  // Accept "YYYY-MM-DD" or full ISO. Strip time, anchor to local midnight.
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  if (Number.isNaN(date.valueOf())) return null;
  return date;
}

function _daysBetween(from, to) {
  const ms = to.valueOf() - from.valueOf();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function _urgencyFor(daysAway) {
  if (daysAway === null) return "unset";
  if (daysAway < 0) return "past";
  if (daysAway <= 3) return "soon";
  return "future";
}

/**
 * Get the canonical Important Dates for a deal.
 *
 * Returns 2 to 4 entries in canonical order. Financing / Closing always
 * render (even when unset) because they apply to every deal. Rescission is
 * opt-in (not every MN P&S contract elects one), so its row is omitted when
 * no rescissionDeadline is set rather than shown as "Not Set". The Inspection
 * Deadline row is omitted when the deal's inspection is declined
 * (deal.inspection === "No"), since there's no inspection to track.
 */
export function getImportantDates(deal) {
  const today = _startOfToday();
  const inspectionDeclined = String(deal?.inspection).toLowerCase() === "no";
  return DATE_FIELDS
    .map(({ field, label }) => {
      const iso = deal?.[field] || "";
      const dateObj = _parseISODate(iso);
      const daysAway = dateObj ? _daysBetween(today, dateObj) : null;
      return {
        field,
        label,
        isoDate: iso,
        dateObj,
        daysAway,
        urgency: _urgencyFor(daysAway),
      };
    })
    .filter(
      (r) => !(r.field === "rescissionDeadline" && r.urgency === "unset"),
    )
    .filter((r) => !(r.field === "inspectionDeadline" && inspectionDeclined));
}

/**
 * Summarize upcoming dates across a set of deals for the dashboard widget.
 * Returns one entry per (deal, date) pair where the date falls inside the
 * window (default 14 days). Past dates and Closed deals are excluded.
 */
export function summarizeUpcomingDates(deals, options = {}) {
  const windowDays = options.windowDays ?? 14;
  const out = [];
  (deals || []).forEach((deal) => {
    if (!deal) return;
    if (deal.dealStage === "Closed") return;
    if (typeof deal.deletedAt === "number" && deal.deletedAt > 0) return;
    const rows = getImportantDates(deal);
    rows.forEach((r) => {
      if (r.urgency === "unset" || r.urgency === "past") return;
      if (r.daysAway > windowDays) return;
      out.push({ deal, ...r });
    });
  });
  out.sort((a, b) => a.daysAway - b.daysAway);
  return out;
}

/**
 * Build a Google Calendar "create event" URL with the given title/date/desc
 * pre-filled. Opening this URL in a new tab lets the user click Save in
 * Google Calendar to commit the event.
 */
export function buildAddToCalendarUrl({ title, isoDate, description }) {
  const m = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  const compact = `${m[1]}${m[2]}${m[3]}`;
  const dates = `${compact}T000000/${compact}T235959`;
  const params = new URLSearchParams({
    text: title,
    dates,
    details: description || "",
  });
  return `https://calendar.google.com/calendar/r/eventedit?${params
    .toString()
    .replace(/%20/g, "+")}`;
}

export { SHORT_LABEL };
